import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Shield, Eye, EyeOff } from 'lucide-react';
import { useSecureAdminAuth } from '@/hooks/useSecureAdminAuth';

interface SecureAdminLoginProps {
  message?: string;
}

const SecureAdminLogin: React.FC<SecureAdminLoginProps> = ({ message }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login } = useSecureAdminAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const success = await login(email, password);
      if (!success) {
        setError('Échec de l\'authentification. Vérifiez vos identifiants ou votre statut administrateur.');
      }
    } catch (error) {
      setError('Une erreur s\'est produite lors de la connexion.');
      console.error('Login error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-feel-blanc-brume flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-feel-bleu-vif rounded-full mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-feel-bleu-vert-fonce mb-2">
            Administration Sécurisée
          </h1>
          <p className="text-feel-gris-doux">
            {message || "Authentification requise pour accéder au panneau d'administration"}
          </p>
        </div>

        <Card className="border-feel-gris-clair shadow-lg">
          <CardHeader className="space-y-1">
            <CardTitle className="text-xl text-feel-bleu-vert-fonce">
              Connexion Administrateur
            </CardTitle>
            <CardDescription className="text-feel-gris-doux">
              Utilisez vos identifiants Supabase pour vous connecter
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-feel-bleu-vert-fonce">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@example.com"
                  required
                  disabled={isSubmitting}
                  className="border-feel-gris-clair focus:border-feel-bleu-vif"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-feel-bleu-vert-fonce">
                  Mot de passe
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    disabled={isSubmitting}
                    className="border-feel-gris-clair focus:border-feel-bleu-vif pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isSubmitting}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-feel-gris-doux" />
                    ) : (
                      <Eye className="h-4 w-4 text-feel-gris-doux" />
                    )}
                  </Button>
                </div>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button 
                type="submit" 
                className="w-full bg-feel-bleu-vif hover:bg-feel-bleu-vif/90 text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Connexion en cours...
                  </>
                ) : (
                  'Se connecter'
                )}
              </Button>
            </form>

            <div className="mt-6 p-4 bg-feel-beige-doux rounded-lg">
              <h3 className="text-sm font-medium text-feel-bleu-vert-fonce mb-2">
                Sécurité renforcée
              </h3>
              <ul className="text-xs text-feel-gris-doux space-y-1">
                <li>• Authentification Supabase obligatoire</li>
                <li>• Vérification du statut administrateur</li>
                <li>• Sessions avec expiration automatique</li>
                <li>• Audit complet des actions</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <div className="text-center mt-6 text-xs text-feel-gris-doux">
          <p>Interface d'administration sécurisée</p>
          <p>Toutes les actions sont enregistrées dans les journaux d'audit</p>
        </div>
      </div>
    </div>
  );
};

export default SecureAdminLogin;